import React from 'react'
import { ImageBackground, ImageSourcePropType } from 'react-native'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import smartSchedulingBackground from '@lyrahealth-inc/ui-core-crossplatform/src/assets/smartSchedulingBackground@2x.png'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Display } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/display/Display'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, DisplaySize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ContentContainer } from '@lyrahealth-inc/ui-core-crossplatform/src/templates/content/ContentContainer'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { mailTo, tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { SchedulingInformation } from './SchedulingInformation'
import { actions, mixpanelEvents } from '../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../mixpanel/mixpanelTracking'
import { SCHEDULING_ACTIVATE } from '../../common/constants/routingConstants'
import { getAuthConfig } from '../../data/auth/authSelectors'
import { DescriptionContainer, LandingHeaderContainer, LandingTitleContainer, SchedulingCTAWrapper } from '../constants'

const TitleImageBackgroundContainer = styled.View(({ theme }) => ({
  paddingBottom: theme.spacing['24px'],
}))

const NewLineBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['8px'],
}))

export const SchedulingLandingBody: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const config = useSelector(getAuthConfig)
  const { isSmartSchedulingOnboardingDisabledForDA } = useFlags()
  const shouldDisableOnboarding =
    isSmartSchedulingOnboardingDisabledForDA && !config?.internationalSmartSchedulingEnabled

  const onStartConnecting = () => {
    track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.CONNECT_CALENDAR_START })
    navigate(SCHEDULING_ACTIVATE.route)
  }

  const instructionsLink = () => {
    track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.SCHEDULING_DA_CONNECTION_INSTRUCTIONS })
    window.open(
      'https://provider-support.lyrahealth.com/hc/en-us/articles/115008263367-Enhance-Your-Experience-With-Smart-Scheduling',
      '_blank',
    )
  }

  return (
    <TitleImageBackgroundContainer>
      <ImageBackground source={smartSchedulingBackground as ImageSourcePropType} accessibilityIgnoresInvertColors>
        <ContentContainer>
          <LandingHeaderContainer>
            <Display level='2' size={DisplaySize.SMALL} text='Calendar' color={colors.textPrimary} />
          </LandingHeaderContainer>
        </ContentContainer>
        <ContentContainer>
          <SchedulingCTAWrapper>
            <LandingTitleContainer>
              <Title
                level='3'
                color={colors.textPrimary}
                size={TitleSize.MEDIUM}
                text={shouldDisableOnboarding ? 'New Lyra Calendar launching soon!' : 'Activate Smart Scheduling'}
              />
            </LandingTitleContainer>
            {shouldDisableOnboarding ? (
              <>
                <BodyText
                  color={colors.textSecondary}
                  size={BodyTextSize.SMALL}
                  text='Thanks for your interest in connecting your calendar! A new and improved calendar will be available to connect to in the coming weeks.'
                />
                <NewLineBodyText
                  color={colors.textSecondary}
                  size={BodyTextSize.SMALL}
                  text={
                    <>
                      Look out for more information in the platform. In the meantime, clients will continue to contact
                      you directly via phone or email to book appointments.{' '}
                      <BodyText
                        bold
                        color={colors.textSecondary}
                        size={BodyTextSize.SMALL}
                        text={
                          <>
                            Still need help? Submit a ticket{' '}
                            <Link
                              text='here'
                              onPress={() =>
                                window.open(
                                  'https://provider-support.lyrahealth.com/hc/en-us/requests/new?ticket_form_id=538748',
                                  '_blank',
                                )
                              }
                              size={BodyTextSize.SMALL}
                            />
                            .
                          </>
                        }
                      />
                    </>
                  }
                />
              </>
            ) : (
              <>
                <DescriptionContainer>
                  {config?.internationalSmartSchedulingEnabled ? (
                    <BodyText
                      color={colors.textSecondary}
                      size={BodyTextSize.SMALL}
                      text={
                        <>
                          Connect your calendar to Lyra so your future clients can book their sessions with you easily
                          and quickly. Get approval from a Lyra representative before you connect a calendar. If you
                          would like to get approval to connect a calendar, please email{' '}
                          <Link
                            text='internationalproviders@lyrahealth.com'
                            onPress={() => {
                              track({
                                event: mixpanelEvents.BUTTON_PRESS,
                                action: actions.SCHEDULING_INTERNATIONAL_APPROVAL_EMAIL,
                              })
                              mailTo('internationalproviders@lyrahealth.com')
                            }}
                            size={BodyTextSize.SMALL}
                          />{' '}
                          for support.
                        </>
                      }
                    />
                  ) : (
                    <BodyText
                      color={colors.textSecondary}
                      size={BodyTextSize.SMALL}
                      text={
                        <>
                          Connect your calendar to Lyra so your future clients can book their sessions with you easily
                          and quickly. First, review{' '}
                          <Link text='these instructions' onPress={instructionsLink} size={BodyTextSize.SMALL} /> in our
                          Help Center. Then, click “Get Started” below.
                        </>
                      }
                    />
                  )}
                </DescriptionContainer>
                <PrimaryButton
                  text='Get started'
                  onPress={onStartConnecting}
                  testID={tID('SchedulingLandingBody-startWalkthrough')}
                />
              </>
            )}
          </SchedulingCTAWrapper>
        </ContentContainer>
        <ContentContainer>
          <LandingTitleContainer>
            <Title level='3' color={colors.textSecondary} size={TitleSize.MEDIUM} text='Why use Smart Scheduling?' />
          </LandingTitleContainer>
        </ContentContainer>
        <ContentContainer>
          <SchedulingInformation />
        </ContentContainer>
      </ImageBackground>
    </TitleImageBackgroundContainer>
  )
}
